import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, RotateCcw, Trash2, XCircle } from 'lucide-react';
import driveApi, { ROLES } from '../api/drive';
import { useDrive } from '../contexts/DriveProvider';
import ItemsView, { useSelection } from '../components/drive/ItemsView';
import DetailsPanel from '../components/drive/DetailsPanel';
import useItemActions from '../components/drive/useItemActions';
import { ToolbarButton, ViewToggle } from './FolderPage';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';

const SOURCES = {
  recent: { title: 'Recent', load: () => driveApi.recent(), empty: 'Files you and your teams edit show up here.' },
  starred: { title: 'Starred', load: () => driveApi.starred(), empty: 'Star files and folders to find them quickly.' },
  shared: { title: 'Shared with me', load: () => driveApi.sharedWithMe(), empty: 'Items colleagues share with you directly appear here.' },
  trash: { title: 'Trash', load: () => driveApi.trashList(), empty: 'Trash is empty.' },
  search: { title: 'Search', load: (q) => driveApi.search(q), empty: 'No files or folders match.' },
};

/** Flat lists that aren't a folder: Recent, Starred, Shared with me, Trash, Search. */
const CollectionPage = ({ source }) => {
  const config = SOURCES[source];
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const { version } = useDrive();
  const [items, setItems] = useState(null);
  const [view, setView] = useState('list');
  const isTrash = source === 'trash';
  const actions = useItemActions({ trashMode: isTrash });
  const selection = useSelection(items || []);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    config.load(q).then((res) => { if (!cancelled) setItems(res); }).catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [source, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh in place after actions without flashing the skeleton
  useEffect(() => {
    if (version === 0) return;
    config.load(q).then(setItems).catch(() => {});
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest('input, textarea, select, [role="dialog"]')) return;
      if (e.key === 'Escape') selection.clear();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selection.selectAll();
      }
      if (isTrash && (e.key === 'Delete' || e.key === 'Backspace') && selection.selectedItems.length) {
        const deletable = selection.selectedItems.filter((i) => i.role >= ROLES.MANAGER);
        if (deletable.length) actions.purge(deletable);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const selected = selection.selectedItems;
  const title = source === 'search' ? `Results for “${q}”` : config.title;

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader
        actions={(
          <>
            {isTrash && selected.length > 0 && (
              <>
                <ToolbarButton icon={RotateCcw} onClick={() => actions.restore(selected.filter((i) => i.role >= ROLES.EDITOR))}>
                  {selected.length > 1 ? `Restore ${selected.length}` : 'Restore'}
                </ToolbarButton>
                {selected.some((i) => i.role >= ROLES.MANAGER) && (
                  <ToolbarButton icon={XCircle} onClick={() => actions.purge(selected.filter((i) => i.role >= ROLES.MANAGER))}>
                    Delete forever
                  </ToolbarButton>
                )}
              </>
            )}
            {isTrash && selected.length === 0 && items?.some((i) => i.role >= ROLES.MANAGER) && (
              <ToolbarButton icon={Trash2} onClick={actions.emptyTrash}>Empty trash</ToolbarButton>
            )}
            {!isTrash && selected.length > 0 && (
              <ToolbarButton icon={Download} onClick={() => actions.download(selected)}>
                {selected.length > 1 ? `Download ${selected.length}` : 'Download'}
              </ToolbarButton>
            )}
            <ViewToggle view={view} setView={setView} />
          </>
        )}
      >
        <PageTitle>{title}</PageTitle>
        {isTrash && (
          <span className="hidden truncate text-[13px] text-muted-foreground sm:inline">
            {selected.length ? `${selected.length} selected` : 'Items you trashed or manage. ⌘A selects all.'}
          </span>
        )}
      </PageHeader>

      <div className="flex flex-1 flex-col lg:flex-row">
        <section aria-label={title} className="flex min-w-0 flex-1 flex-col px-4 py-4 sm:px-6">
          {items === null && (
            <div className="flex flex-col gap-1.5" aria-busy="true">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />)}
            </div>
          )}
          {items?.length === 0 && (
            <p className="mx-auto mt-16 max-w-sm text-center text-[13.5px] text-muted-foreground">{config.empty}</p>
          )}
          {items?.length > 0 && (
            <ItemsView view={view} items={items} selection={selection} actions={actions} showLocation />
          )}
        </section>
        {!isTrash && (
          <div className="border-t border-border px-4 py-5 sm:px-6 lg:sticky lg:top-14 lg:h-[calc(100vh-3.5rem)] lg:w-[320px] lg:shrink-0 lg:overflow-y-auto lg:border-l lg:border-t-0 lg:px-5">
            <DetailsPanel item={selected.length === 1 ? selected[0] : null} onShare={actions.share} selectionCount={selected.length} />
          </div>
        )}
      </div>
      {actions.dialogs}
    </div>
  );
};

export default CollectionPage;
